import { useMemo } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import intl from "react-intl-universal";
import { TbArrowDownLeft, TbArrowUpRight, TbChartBar } from "react-icons/tb";
import { useColorMode } from "../../../Hooks/useColorMode";
import { formatCurrency } from "../../../Utils/formatters";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const readBrandAccent = () => {
  if (typeof window === "undefined") return "#e7a73f";
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--brand-accent")
    .trim();
  return value || "#e7a73f";
};

const readBrandAccentSoft = () => {
  if (typeof window === "undefined") return "rgba(231, 167, 63, 0.12)";
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--brand-accent-soft")
    .trim();
  return value || "rgba(231, 167, 63, 0.12)";
};

const EMERALD = "#10b981";

const formatReaisFromCents = (cents) => formatCurrency(Math.abs(Number(cents) || 0));

const buildBarOptions = (isDark) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: isDark ? "#333" : "#242424",
      titleFont: { size: 12, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        label: (context) => formatCurrency(Math.round((context.parsed.x || 0) * 100)),
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        color: isDark ? "rgba(255,255,255,0.06)" : "rgba(229,231,235,0.7)",
        drawBorder: false,
      },
      ticks: {
        color: isDark ? "#9ca3af" : "#9ca3af",
        font: { size: 10 },
        callback: (value) => {
          if (value >= 1000) return `${Math.round(value / 1000)}k`;
          return value;
        },
      },
      border: { display: false },
    },
    y: {
      grid: { display: false },
      ticks: {
        color: isDark ? "#d1d5db" : "#6b7280",
        font: { size: 11, weight: "600" },
      },
      border: { display: false },
    },
  },
});

const buildLineOptions = (isDark) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      align: "start",
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        pointStyle: "circle",
        padding: 12,
        color: isDark ? "#9ca3af" : "#6b7280",
        font: { size: 10 },
      },
    },
    tooltip: {
      backgroundColor: isDark ? "#333" : "#242424",
      titleFont: { size: 11, weight: "600" },
      bodyFont: { size: 11 },
      padding: 8,
      cornerRadius: 6,
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || "";
          return `${label}: ${formatCurrency(Math.round((context.parsed.y || 0) * 100))}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: isDark ? "rgba(255,255,255,0.06)" : "rgba(229,231,235,0.55)",
        drawBorder: false,
      },
      ticks: {
        color: isDark ? "#6b7280" : "#9ca3af",
        font: { size: 10 },
        maxRotation: 0,
        maxTicksLimit: 8,
      },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      display: false,
      grid: { display: false },
      border: { display: false },
    },
  },
  elements: {
    line: { tension: 0.35, borderWidth: 2 },
    point: { radius: 0, hoverRadius: 4, hitRadius: 10 },
  },
});

const ChartPanel = ({ title, subtitle, children, className = "" }) => (
  <div
    className={`summary-card flex min-h-[220px] flex-col overflow-hidden ${className}`.trim()}
  >
    <div className="mb-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="m-0 text-sm font-bold text-brand-dark dark:text-gray-100">{title}</p>
        {subtitle ? (
          <p className="mt-0.5 m-0 text-[11px] text-gray-500 dark:text-gray-400">{subtitle}</p>
        ) : null}
      </div>
      <div className="summary-card-icon shrink-0">
        <TbChartBar className="h-4 w-4" aria-hidden />
      </div>
    </div>
    <div className="min-h-0 flex-1">{children}</div>
  </div>
);

const MetricChip = ({ icon: Icon, label, value, tone }) => {
  const isOut = tone === "out";

  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 dark:border-brand-dark-border dark:bg-brand-dark-surface/80">
      <div
        className={`home-activity-metric__icon-wrap flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
          isOut
            ? "home-activity-metric--out bg-brand-gold/15"
            : "bg-emerald-500/10"
        }`}
      >
        <Icon
          className={`h-3.5 w-3.5 ${
            isOut
              ? "home-activity-metric--out text-[color:var(--brand-accent-muted,#9a7340)] dark:text-[color:var(--brand-accent,#e7a73f)]"
              : "text-emerald-600 dark:text-emerald-400"
          }`}
          aria-hidden
        />
      </div>
      <div className="min-w-0">
        <p className="m-0 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p
          className={`m-0 truncate text-sm font-bold tabular-nums ${
            isOut
              ? "home-activity-metric--out text-[color:var(--brand-accent-muted,#9a7340)] dark:text-[color:var(--brand-accent,#e7a73f)]"
              : "text-emerald-600 dark:text-emerald-400"
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
};

export const HomeActivityCharts = ({
  entradaCents = 0,
  saidaCents = 0,
  dailySeries,
  periodLabel = "",
  loading = false,
}) => {
  const { isDark } = useColorMode();

  const barOptions = useMemo(() => buildBarOptions(isDark), [isDark]);
  const lineOptions = useMemo(() => buildLineOptions(isDark), [isDark]);
  const accentOut = readBrandAccent();
  const accentOutSoft = readBrandAccentSoft();

  const entradaLabel = intl.get("dashboard.activityCharts.pixIn");
  const saidaLabel = intl.get("dashboard.activityCharts.pixOut");

  const barData = useMemo(
    () => ({
      labels: [entradaLabel, saidaLabel],
      datasets: [
        {
          data: [(entradaCents || 0) / 100, (saidaCents || 0) / 100],
          backgroundColor: [EMERALD, accentOut],
          borderRadius: 6,
          barThickness: 22,
          maxBarThickness: 28,
        },
      ],
    }),
    [entradaCents, saidaCents, entradaLabel, saidaLabel, accentOut],
  );

  const lineData = useMemo(() => {
    const labels = dailySeries?.labels?.length
      ? dailySeries.labels
      : ["—"];
    const entrada = dailySeries?.entrada?.length
      ? dailySeries.entrada
      : [0];
    const saida = dailySeries?.saida?.length
      ? dailySeries.saida
      : [0];

    return {
      labels,
      datasets: [
        {
          label: entradaLabel,
          data: entrada,
          borderColor: EMERALD,
          backgroundColor: "rgba(16, 185, 129, 0.12)",
          fill: true,
          pointBackgroundColor: EMERALD,
        },
        {
          label: saidaLabel,
          data: saida,
          borderColor: accentOut,
          backgroundColor: accentOutSoft,
          fill: true,
          pointBackgroundColor: accentOut,
        },
      ],
    };
  }, [dailySeries, entradaLabel, saidaLabel, accentOut, accentOutSoft]);

  if (loading) {
    return (
      <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="summary-card h-[220px] animate-pulse bg-gray-50 dark:bg-brand-dark-elevated" />
        <div className="summary-card h-[220px] animate-pulse bg-gray-50 dark:bg-brand-dark-elevated" />
      </div>
    );
  }

  return (
    <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
      <ChartPanel
        title={intl.get("dashboard.activityCharts.pixFlowTitle")}
        subtitle={periodLabel || intl.get("dashboard.activityCharts.selectedPeriod")}
      >
        <div className="mb-3 grid grid-cols-2 gap-2">
          <MetricChip
            icon={TbArrowDownLeft}
            label={entradaLabel}
            value={formatReaisFromCents(entradaCents)}
            tone="in"
          />
          <MetricChip
            icon={TbArrowUpRight}
            label={saidaLabel}
            value={formatReaisFromCents(saidaCents)}
            tone="out"
          />
        </div>
        <div className="h-[110px] w-full">
          <Bar data={barData} options={barOptions} />
        </div>
      </ChartPanel>

      <ChartPanel
        title={intl.get("dashboard.activityCharts.dailyTitle")}
        subtitle={intl.get("dashboard.activityCharts.dailySubtitle")}
      >
        <div className="h-[168px] w-full">
          <Line data={lineData} options={lineOptions} />
        </div>
      </ChartPanel>
    </div>
  );
};
